import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { AppShell } from '@/components/layout/AppShell'
import { ImpersonationBanner } from '@/components/layout/ImpersonationBanner'

// Auth pages
import Login from './auth/Login'
import Register from './auth/Register'
import AcceptInvitation from './auth/AcceptInvitation'

// Landing page
import Landing from './Landing'
import { CaseStudiesList, CaseStudyDetail } from './CaseStudies'
import { BlogList, BlogPost } from './Blog'
import { SolutionsList, IndustryDetail, FunctionDetail } from './Solutions'
import { CompareList, CompareDetail } from './Compare'
import { GlossaryList, GlossaryDetail } from './Glossary'
import ROICalculator from './ROICalculator'
import { ExtractList, ExtractDetail } from './ExtractPages'
import OGImage from './OGImage'

// Main pages
import Dashboard from './dashboard/Dashboard'
import ProjectList from './projects/ProjectList'
import ProjectDetail from './projects/ProjectDetail'
import NewProject from './projects/NewProject'
import Plans from './billing/Plans'
import WalletPage from './wallet/Wallet'
import Usage from './usage/Usage'
import Profile from './settings/Profile'
import OrgSettings from './settings/OrgSettings'
import Members from './settings/Members'

// Admin pages
import AdminDashboard from './admin/Dashboard'
import AdminUsers from './admin/Users'
import AdminOrganizations from './admin/Organizations'
import AdminBillingPlans from './admin/BillingPlans'
import AdminAuthLogs from './admin/AuthLogs'
import NotFound from './NotFound'

function RequireAuth() {
  const { isAuthenticated } = useAuth()
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return (
    <>
      <ImpersonationBanner />
      <Outlet />
    </>
  )
}

function RequireAdmin() {
  const { user } = useAuth()
  if (!user?.is_superadmin) {
    return <Navigate to="/dashboard" replace />
  }
  return <Outlet />
}

function RequireOrgRole({ roles }: { roles: string[] }) {
  const { user, activeOrgRole } = useAuth()
  // Superadmin can access everything
  if (user?.is_superadmin) return <Outlet />
  if (!activeOrgRole || !roles.includes(activeOrgRole)) {
    return <Navigate to="/dashboard" replace />
  }
  return <Outlet />
}

function RedirectIfAuth() {
  const { isAuthenticated } = useAuth()
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }
  return <Outlet />
}

function LandingOrDashboard() {
  const { isAuthenticated } = useAuth()
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }
  return <Landing />
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <LandingOrDashboard />,
  },
  {
    path: '/case-studies',
    element: <CaseStudiesList />,
  },
  {
    path: '/case-studies/:slug',
    element: <CaseStudyDetail />,
  },
  {
    path: '/blog',
    element: <BlogList />,
  },
  {
    path: '/blog/:slug',
    element: <BlogPost />,
  },
  {
    path: '/solutions',
    element: <SolutionsList />,
  },
  {
    path: '/solutions/industry/:slug',
    element: <IndustryDetail />,
  },
  {
    path: '/solutions/function/:slug',
    element: <FunctionDetail />,
  },
  {
    path: '/compare',
    element: <CompareList />,
  },
  {
    path: '/compare/:slug',
    element: <CompareDetail />,
  },
  {
    path: '/glossary',
    element: <GlossaryList />,
  },
  {
    path: '/glossary/:slug',
    element: <GlossaryDetail />,
  },
  {
    path: '/roi-calculator',
    element: <ROICalculator />,
  },
  {
    path: '/extract',
    element: <ExtractList />,
  },
  {
    path: '/extract/:slug',
    element: <ExtractDetail />,
  },
  {
    element: <RedirectIfAuth />,
    children: [
      { path: '/login', element: <Login /> },
      { path: '/register', element: <Register /> },
    ],
  },
  {
    // Invitations are accessible whether or not the user is logged in
    path: '/invitations/:token',
    element: <AcceptInvitation />,
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/dashboard', element: <Dashboard /> },
          { path: '/projects', element: <ProjectList /> },
          { path: '/projects/new', element: <NewProject /> },
          { path: '/projects/:projectId', element: <ProjectDetail /> },
          { path: '/settings', element: <Profile /> },
          // Role-gated routes — direct URL also blocked
          {
            element: <RequireOrgRole roles={['org_owner', 'org_billing']} />,
            children: [
              { path: '/billing', element: <Plans /> },
              { path: '/wallet', element: <WalletPage /> },
            ],
          },
          {
            element: <RequireOrgRole roles={['org_owner', 'org_admin', 'org_billing']} />,
            children: [
              { path: '/usage', element: <Usage /> },
            ],
          },
          {
            element: <RequireOrgRole roles={['org_owner', 'org_admin']} />,
            children: [
              { path: '/settings/org', element: <OrgSettings /> },
              { path: '/settings/members', element: <Members /> },
            ],
          },
          {
            element: <RequireAdmin />,
            children: [
              { path: '/admin', element: <AdminDashboard /> },
              { path: '/admin/users', element: <AdminUsers /> },
              { path: '/admin/organizations', element: <AdminOrganizations /> },
              { path: '/admin/billing-plans', element: <AdminBillingPlans /> },
              { path: '/admin/auth-logs', element: <AdminAuthLogs /> },
            ],
          },
        ],
      },
    ],
  },
  {
    // Internal route — rendered only by scripts/prerender.mjs to generate
    // per-page social-card PNGs. Not in sitemap, not crawled.
    path: '/__og/:type/:slug',
    element: <OGImage />,
  },
  {
    // True 404 — shows a proper not-found page with noindex meta so Google
    // doesn't index junk URLs. Previously this silently redirected to
    // /dashboard, which Google treats as a soft 404 (penalty risk).
    path: '*',
    element: <NotFound />,
  },
])
